'use client';
import { useEffect, useState, useRef } from 'react';
import { IDataFrameSchema } from '@/lib/dataFrameTypes';
import { TKeyValuePair } from '@/types';
import ErrorBoundary from '@/components/ErrorBoundary';
import SelectCellCta from './SelectCellCta';
import useDataframeContext from '../../DataframeContext';
import RowActions from './RowActions';
import CellDetails from './CellDetails';
import { SCHEMA_DISPLAY_FIELD_NAME_BLACKLIST, SYSTEM_ROW_ID_FIELD_NAME } from '../../consts';
import * as Styled from './styled';
import { Row } from '@/ui/primatives';
import { OpenInNewWindowIcon, CaretUpIcon, CaretDownIcon } from '@radix-ui/react-icons';
import Button from '@/ui/Button';

import OxenModal from '@/ui/Modal';
import { Simple as Tooltip } from '@/ui/Tooltip';
import { useModal } from 'react-hooks-use-modal';
import { ROOT_LAYOUT_DOM_NODE_ID } from '@/config/appSettings';

type RowDetailsProps = {
  schema: IDataFrameSchema;
  selectedRow: any;
  selectedRowIndex: number;
  // rowCount: number;
  selectedColumn: any;
  onCellSelect: ({ rowIndex, columnKey }: { rowIndex: number; columnKey: string }) => void;
  onRowIncrement?: () => void;
  onRowDecrement?: () => void;
};

const sortRowToMatchSchema = (schema: IDataFrameSchema, row: any) => {
  const sortOrder = schema.fields.map((field) => field.name);
  const tmpRow: TKeyValuePair = {};
  if (!!row) {
    sortOrder.forEach((key) => {
      tmpRow[key] = row[key];
    });
  }
  return tmpRow;
};

type RowNavigationProps = {
  style?: React.CSSProperties;
  onRowIncrement?: () => void;
  onRowDecrement?: () => void;
  children?: React.ReactNode;
};

const RowNavigation: React.FC<RowNavigationProps> = ({ onRowIncrement, onRowDecrement, children, style }) => {
  return (
    <Styled.RowNavigation>
      {/* <Row gap={20} alignItems="center" justifyContent="space-between" style={style}> */}
      {children}
      <Row gap={0} alignItems="center">
        <Tooltip content="Previous Row">
          <Button.Text tiny onClick={onRowDecrement}>
            <CaretUpIcon style={{ height: 24, width: 24 }} />
          </Button.Text>
        </Tooltip>
        <Tooltip content="Next Row">
          <Button.Text tiny onClick={onRowIncrement}>
            <CaretDownIcon style={{ height: 24, width: 24 }} />
          </Button.Text>
        </Tooltip>
      </Row>
      {/* </Row> */}
    </Styled.RowNavigation>
  );
};

const RowDetails: React.FC<RowDetailsProps> = ({
  schema,
  selectedRow,
  selectedRowIndex,
  // rowCount,
  selectedColumn,
  onCellSelect,
  onRowIncrement,
  onRowDecrement,
}) => {
  const fileRootPath = selectedColumn?.fileRootPath;
  const sortedRow = sortRowToMatchSchema(schema, selectedRow);
  const scrollRef = useRef<null | HTMLDivElement>(null);

  const [Modal, open, close, isOpen] = useModal(ROOT_LAYOUT_DOM_NODE_ID, {
    preventScroll: true,
  });

  const { viewData, rows, columns, updateRow } = useDataframeContext();
  const dataFrame = viewData;

  const rowCount = dataFrame?.pagination?.total_entries;
  const selectedRowNumber =
    (dataFrame?.pagination?.page_number - 1) * dataFrame?.pagination?.page_size + selectedRowIndex + 1;

  const [originRow, setOriginRow] = useState<any>(selectedRow);
  const [workingRow, setWorkingRow] = useState<any>(selectedRow);

  useEffect(() => {
    // Only staging DF's have the oxen identifier - only use it if it exists, falling back to the selected row
    const rowHasOxenIdentifier = selectedRow?.[SYSTEM_ROW_ID_FIELD_NAME];
    const currentRowById = rows.find(
      (row: TKeyValuePair) => row[SYSTEM_ROW_ID_FIELD_NAME] === selectedRow?.[SYSTEM_ROW_ID_FIELD_NAME],
    );
    const currentRow = rowHasOxenIdentifier ? currentRowById : selectedRow;
    setOriginRow(currentRow);
    setWorkingRow(currentRow);
  }, [selectedRow, rows]);

  const selectedRowExists = rows.some(
    (row: TKeyValuePair) => row?.[SYSTEM_ROW_ID_FIELD_NAME] === originRow?.[SYSTEM_ROW_ID_FIELD_NAME],
  );

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, [selectedColumn]);

  return (
    <>
      {!isOpen && (
        <Styled.RowDetails>
          {!!workingRow && !!selectedColumn && !!selectedRowExists ? (
            <ErrorBoundary containerComponent="DataGrid > RowDetails">
              <div
                style={{
                  position: 'sticky',
                  top: 0,
                  background: 'var(--color-secondary-lightGray1)',
                  borderBottom: '1px solid var(--color-secondary-lightGray3)',
                  zIndex: 1,
                }}
              >
                <RowNavigation
                  onRowIncrement={onRowIncrement}
                  onRowDecrement={onRowDecrement}
                  style={{ padding: '5px 10px 0 10px' }}
                >
                  <Button.Text tiny onClick={open}>
                    <Tooltip content="Open Full Screen View">
                      <OpenInNewWindowIcon style={{ height: 22, width: 22 }} />
                    </Tooltip>
                  </Button.Text>
                </RowNavigation>
              </div>

              <Styled.Body>
                <RowActions row={originRow} workingRow={workingRow} commitChanges={() => updateRow(workingRow)} />
                {Object.keys(sortedRow)
                  .filter((key) => !SCHEMA_DISPLAY_FIELD_NAME_BLACKLIST.includes(key))
                  .map((key: string) => (
                    <div key={key} ref={selectedColumn.key === key ? scrollRef : null}>
                      <CellDetails
                        schema={schema}
                        row={workingRow}
                        onClick={() => onCellSelect({ rowIndex: selectedRowIndex, columnKey: key })}
                        onChange={setWorkingRow}
                        editable={columns.find((column: any) => column.key === key).editable}
                        columnKey={key}
                        fileRootPath={fileRootPath}
                        selected={selectedColumn.key === key}
                      />
                    </div>
                  ))}
              </Styled.Body>
            </ErrorBoundary>
          ) : (
            <Styled.RowDetailsPlaceholder>
              <SelectCellCta />
            </Styled.RowDetailsPlaceholder>
          )}
        </Styled.RowDetails>
      )}
      <OxenModal
        Modal={Modal}
        isOpen={isOpen}
        close={close}
        style={{
          width: '90vw',
          height: '90vh',
          maxWidth: '100%',
          background: 'var(--color-secondary-lightGray1)',
        }}
      >
        <OxenModal.Header
          style={{
            borderBottom: '1px solid var(--color-secondary-lightGray3)',
            background: 'var(--color-secondary-lightGray2)',
          }}
        >
          <RowNavigation onRowIncrement={onRowIncrement} onRowDecrement={onRowDecrement}>
            <div>
              Row <b>{selectedRowNumber}</b> of {rowCount}
            </div>
          </RowNavigation>
        </OxenModal.Header>
        <OxenModal.Body style={{ overflowY: 'auto', padding: '10px 40px' }}>
          {Object.keys(sortedRow)
            .filter((key) => !SCHEMA_DISPLAY_FIELD_NAME_BLACKLIST.includes(key))
            .map((key: string) => (
              <div key={key} ref={selectedColumn.key === key ? scrollRef : null}>
                <CellDetails
                  schema={schema}
                  row={workingRow}
                  onClick={() => onCellSelect({ rowIndex: selectedRowIndex, columnKey: key })}
                  onChange={setWorkingRow}
                  editable={columns.find((column: any) => column.key === key).editable}
                  columnKey={key}
                  fileRootPath={fileRootPath}
                  selected={selectedColumn.key === key}
                />
              </div>
            ))}
        </OxenModal.Body>
      </OxenModal>
    </>
  );
};

export default RowDetails;
